import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  ReactRenderer,
  useEditor,
  useEditorState,
  type Editor,
  type NodeViewProps,
} from '@tiptap/react';
import { Extension, InputRule, Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { useApolloClient } from '@apollo/client/react';
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model';
import { AllSelection, NodeSelection, Plugin, Selection, TextSelection, type Transaction } from '@tiptap/pm/state';
import { GapCursor } from '@tiptap/pm/gapcursor';
import type { EditorView } from '@tiptap/pm/view';
import { Markdown } from '@tiptap/markdown';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { lowlight } from '../lib/highlight';
import { createCodeBlockView } from './CodeBlockView';
import type { MentionUser } from '../lib/access';
import { Icon } from './Icon';
import { FileEmbed } from './Markdown';
import {
  FILE_REF_SCHEME,
  fileIdOf,
  setUploadProgress,
  UPLOAD_SERVICE_DOWN_HINT,
  uploadFile,
  useAttachTarget,
  useFilesConfig,
  useUploadProgress,
  useUploadService,
} from '../lib/files';
import {
  MentionMd,
  escapeBlockStarts,
  markdownExtensions,
  normalizeHref,
  planPlainTextPaste,
  prepareMarkdown,
  tidyMarkdown,
} from './richEditorMarkdown';

/**
 * WYSIWYG markdown editor (TipTap) for task descriptions, comments and decisions —
 * GitLab's rich-text mode: a fixed toolbar above the text (no undo/redo or @ buttons —
 * ⌘Z / ⌘⇧Z and typing `@` do those, as in GitLab), formatting shown live
 * (StarterKit input rules still accept `**x**`, `# `, `- [ ] `), @-type to mention
 * staff. Stores MARKDOWN (via @tiptap/markdown) so the backend/audit/timeline are
 * unchanged; the timeline renders that markdown with <Markdown>.
 *
 * What round-trips is decided by `markdownExtensions` in ./richEditorMarkdown, which
 * `scripts/check-rich-editor-md.mjs` exercises.
 */

// @tiptap/markdown adds editor.markdown (a manager with parse/serialize). Type the bits we use.
type MdEditor = Editor & {
  markdown: { parse: (md: string) => object; serialize: (doc: object) => string };
};
/** The stored form: serialised, block markers in plain text escaped, output tidied. */
const toMarkdown = (e: Editor) =>
  tidyMarkdown((e as MdEditor).markdown.serialize(escapeBlockStarts(e.getJSON())));

export interface RichEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  /** Read-only: toolbar disabled, text not editable, muted look. */
  disabled?: boolean;
  mentionUsers?: MentionUser[];
  /** Cmd/Ctrl+Enter. */
  onSubmit?: () => void;
  /** Escape, when no @mention suggestion is open. */
  onCancel?: () => void;
  /** Focus at the end of the text on mount. */
  autoFocus?: boolean;
  /**
   * True while a file upload is in flight (bytes still going up) — the parent blocks its
   * Save/Comment button. A file Transforge is still processing does not count: its ref is
   * already in the text and saving is fine.
   */
  onUploadingChange?: (uploading: boolean) => void;
}

/** Toolbar glyphs not in the app icon set — same contract as <Icon>: 16px, stroke-only, currentColor. */
const GLYPHS = {
  link: 'M6.6 9.4l2.8-2.8M7.3 4.6l1-1a2.6 2.6 0 013.7 3.7l-1 1M8.7 11.4l-1 1a2.6 2.6 0 01-3.7-3.7l1-1',
  bullet: 'M6 4h7.5M6 8h7.5M6 12h7.5M2.6 4h.1M2.6 8h.1M2.6 12h.1',
  ordered: 'M7 4h6.5M7 8h6.5M7 12h6.5M2.2 2.8h1v3M2 9.4c.2-.6 1.8-.7 1.8.3 0 .8-1.8 1.3-1.8 2.3h2',
  task: 'M2 3.2h3.6v3.6H2zM2.3 11.4l1.2 1.2 2.2-2.4M8 5h5.5M8 11.4h5.5',
  code: 'M5.5 4.5L2 8l3.5 3.5M10.5 4.5L14 8l-3.5 3.5',
  codeBlock: 'M2.5 2.5h11v11h-11zM6.3 6.3L4.6 8l1.7 1.7M9.7 6.3L11.4 8 9.7 9.7',
  attach: 'M13.2 7.4l-5.3 5.3a3.2 3.2 0 01-4.5-4.5l5.6-5.6a2.1 2.1 0 013 3l-5.5 5.5a1.05 1.05 0 01-1.5-1.5l5-5',
} as const;
function Glyph({ d }: { d: string }) {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function RichEditor({
  value,
  onChange,
  placeholder,
  disabled,
  mentionUsers = [],
  onSubmit,
  onCancel,
  autoFocus,
  onUploadingChange,
}: RichEditorProps) {
  // ── attachments: on when a page provides an AttachTarget and the api has storage ──
  const attach = useAttachTarget();
  const client = useApolloClient();
  const filesCfg = useFilesConfig(!attach);
  const [uploads, setUploads] = useState(0);
  const uploadsRef = useRef(0);
  uploadsRef.current = uploads;
  const [fileError, setFileError] = useState<{ id: number; text: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // SCIO health gate: Attach stays disabled until this browser can reach Transforge
  const uploadService = useUploadService(attach && filesCfg?.uploadsEnabled ? filesCfg.transforgeUrl : null);
  const canAttach =
    !!attach && attach.taskId != null && !!filesCfg?.uploadsEnabled && uploadService === 'ok' && !disabled;
  const filesCfgRef = useRef(filesCfg);
  filesCfgRef.current = filesCfg;
  const canAttachRef = useRef(canAttach);
  canAttachRef.current = canAttach;
  // set once the editor exists; paste/drop handlers (built once) call through it
  const startUploads = useRef<(files: File[], at?: { from: number; to: number }) => void>(() => {});
  // the selection when the file picker opened — the picked files go there, not wherever focus() lands
  const pickAt = useRef<{ from: number; to: number } | null>(null);
  const onUploadingRef = useRef(onUploadingChange);
  onUploadingRef.current = onUploadingChange;
  useEffect(() => {
    onUploadingRef.current?.(uploads > 0);
  }, [uploads]);
  useEffect(() => () => onUploadingRef.current?.(false), []);
  useEffect(() => {
    if (!fileError) return;
    const t = setTimeout(() => setFileError((cur) => (cur?.id === fileError.id ? null : cur)), 9000);
    return () => clearTimeout(t);
  }, [fileError]);

  // Suggestion config is created once (editor is created once) but must read the
  // latest fetched users — keep them in a ref the items() callback reads.
  const usersRef = useRef<MentionUser[]>(mentionUsers);
  usersRef.current = mentionUsers;
  // Same for the key handlers, which live in an extension built once.
  const keysRef = useRef({ onSubmit, onCancel });
  keysRef.current = { onSubmit, onCancel };
  const suggestionOpen = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Dirty tracking. `baseline` is the value the editor was (re)loaded from plus the
  // editor's own serialisation of it; while the document serialises to that, the editor
  // reports the ORIGINAL raw value, so opening and saving an untouched (or edited-then-
  // undone) description is a no-op for the parent rather than a normalised rewrite.
  // `lastEmitted` tells the parent's echo of our own onChange apart from a real external
  // value (e.g. reset to '' after submit) — only the latter reloads the document.
  const baseline = useRef<{ raw: string; md: string | null }>({ raw: value, md: null });
  const lastEmitted = useRef(value);

  const editor = useEditor({
    editable: !disabled,
    // autofocus is done below, not via TipTap's `autofocus: 'end'`: on touch devices its
    // focus command dispatches a transaction a frame late, after the focus event already
    // moved the state on → "Applying a mismatched transaction".
    autofocus: false,
    extensions: [
      // codeBlock → CodeBlockLowlight (syntax highlighting + the per-block language picker NodeView).
      ...markdownExtensions(
        CodeBlockLowlight.extend({
          addNodeView() {
            return createCodeBlockView;
          },
        }).configure({ lowlight }),
        MentionMd.configure({
          HTMLAttributes: { class: 'mention-chip' },
          renderText: ({ node }) => `@${node.attrs.id}`,
          suggestion: {
            items: ({ query }) => {
              const q = query.toLowerCase();
              return usersRef.current
                .filter((u) => u.email.toLowerCase().includes(q) || (u.displayName ?? '').toLowerCase().includes(q))
                .slice(0, 50);
            },
            render: () => makeMentionRenderer(suggestionOpen),
          },
        }),
        InfrasFile.configure({ inline: false }),
      ),
      UploadPlaceholder,
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      ListTypeSwitch,
      EmptyDocCaret,
      Extension.create({
        name: 'richEditorKeys',
        addKeyboardShortcuts() {
          return {
            'Mod-Enter': () => {
              if (!keysRef.current.onSubmit) return false;
              // never submit while an upload is still going up — its ref is not in the text yet
              if (uploadsRef.current > 0) return true;
              keysRef.current.onSubmit();
              return true;
            },
            Escape: () => {
              if (suggestionOpen.current || !keysRef.current.onCancel) return false;
              keysRef.current.onCancel();
              return true;
            },
            // A GFM table cell holds one line of inline text: Enter there used to split the
            // cell into paragraphs, which the serializer joins with U+001F. Swallow it (and
            // Shift+Enter, whose hard break saves as a plain space) — GitLab does the same.
            Enter: ({ editor: e }) => !suggestionOpen.current && inTableCell(e),
            'Shift-Enter': ({ editor: e }) => inTableCell(e),
            // Two spaces inside a code block; anywhere else Tab moves focus out of the
            // editor (list items keep StarterKit's Tab-to-indent).
            Tab: ({ editor: e }) => (e.isActive('codeBlock') ? e.commands.insertContent('  ') : false),
          };
        },
      }),
    ],
    editorProps: {
      // Files on the clipboard / dropped files become attachments (when attaching is on).
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        if (!files.length || !canAttachRef.current) {
          // pasting over a selected attachment must not replace it: paste after it instead
          caretOffSelectedFile(_view);
          return false;
        }
        event.preventDefault();
        const { from, to } = _view.state.selection;
        startUploads.current(files, { from, to });
        return true;
      },
      // A click on an attachment node-selects it — explicitly, not via the draggable-leaf default.
      handleClickOn: (view, _pos, node, nodePos, _event, direct) => {
        if (!direct || node.type.name !== 'image' || !view.editable) return false;
        view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)));
        view.focus();
        return true;
      },
      // A clicked attachment is node-selected (highlighted). Typing then goes AFTER it —
      // only Backspace/Delete remove it (undo restores) — instead of silently replacing it.
      handleTextInput: (view, _from, _to, text) => {
        const sel = view.state.selection;
        if (!(sel instanceof NodeSelection) || sel.node.type.name !== 'image') return false;
        const tr = view.state.tr;
        const after = sel.to;
        // into the empty line that follows, else a new line of its own (never glued onto the next text)
        const next = tr.doc.resolve(after).nodeAfter;
        if (next?.isTextblock && !next.content.size) tr.insertText(text, after + 1);
        else tr.insert(after, view.state.schema.nodes.paragraph.create(null, view.state.schema.text(text)));
        tr.setSelection(TextSelection.create(tr.doc, after + 1 + text.length));
        view.dispatch(tr.scrollIntoView());
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (moved || !files.length || !canAttachRef.current) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        startUploads.current(files, pos == null ? undefined : { from: pos, to: pos });
        return true;
      },
      // Multi-line plain text (no HTML on the clipboard): keep the line structure
      // instead of one paragraph per line; clear code becomes one code block. Pastes
      // inside a code block never reach this (ProseMirror inserts them verbatim).
      clipboardTextParser: (text, _ctx, _plain, view) => {
        if (!/\r|\n/.test(text.replace(/\s+$/, ''))) return null as unknown as Slice;
        const { schema } = view.state;
        const plan = planPlainTextPaste(text);
        if (plan.kind === 'code') {
          return new Slice(Fragment.from(schema.nodes.codeBlock.create(null, schema.text(plan.text))), 0, 0);
        }
        const paras = plan.paragraphs.map((lines) =>
          schema.nodes.paragraph.create(
            null,
            lines.flatMap((l, i) => (i ? [schema.nodes.hardBreak.create(), schema.text(l)] : [schema.text(l)])),
          ),
        );
        return new Slice(Fragment.from(paras), 1, 1);
      },
    },
    content: prepareMarkdown(value),
    // parse `content` above as markdown (option consumed by @tiptap/markdown)
    ...({ contentType: 'markdown' } as object),
    onCreate: ({ editor: e }) => {
      baseline.current.md = toMarkdown(e);
    },
    onUpdate: ({ editor: e }) => {
      const md = toMarkdown(e);
      const out = md === baseline.current.md ? baseline.current.raw : md;
      if (out === lastEmitted.current) return;
      lastEmitted.current = out;
      onChangeRef.current(out);
    },
  });

  // External value changes (e.g. parent resets to '' after submit) → reload once, silently.
  // Our own onChange echoing back is NOT external: reloading on it raced the typing
  // (a stale value reset the doc mid-keystroke).
  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    editor.commands.setContent((editor as MdEditor).markdown.parse(prepareMarkdown(value)), { emitUpdate: false });
    baseline.current = { raw: value, md: toMarkdown(editor) };
    lastEmitted.current = value;
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor || !autoFocus) return;
    editor.view.dispatch(editor.state.tr.setSelection(Selection.atEnd(editor.state.doc)).scrollIntoView());
    editor.view.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // TipTap 3 does not re-render on transactions; subscribe to exactly what the toolbar
  // shows. Marks reflect the selection only while focused: an unfocused editor still
  // has a stored selection, which showed a stuck "active" mark.
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      const f = e.isFocused;
      const on = (name: string, attrs?: Record<string, unknown>) => f && e.isActive(name, attrs);
      return {
        bold: on('bold'),
        italic: on('italic'),
        strike: on('strike'),
        code: on('code'),
        codeBlock: on('codeBlock'),
        bulletList: on('bulletList'),
        orderedList: on('orderedList'),
        taskList: on('taskList'),
        blockquote: on('blockquote'),
        link: on('link'),
        heading: !f
          ? '0'
          : e.isActive('heading', { level: 1 })
          ? '1'
          : e.isActive('heading', { level: 2 })
            ? '2'
            : e.isActive('heading', { level: 3 })
              ? '3'
              : '0',
      };
    },
  });

  // Link popover — replaces window.prompt.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [linkError, setLinkError] = useState('');
  const linkInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (linkOpen) linkInput.current?.select();
  }, [linkOpen]);

  // A toolbar too narrow for its buttons (phone) scrolls sideways; fade the clipped edge(s).
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [clip, setClip] = useState({ start: false, end: false });
  const ready = !!editor && !!st;
  useEffect(() => {
    const t = toolbarRef.current;
    if (!t) return;
    const measure = () => {
      const start = t.scrollLeft > 1;
      const end = t.scrollLeft + t.clientWidth < t.scrollWidth - 1;
      setClip((c) => (c.start === start && c.end === end ? c : { start, end }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(t);
    t.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      t.removeEventListener('scroll', measure);
    };
  }, [ready]);

  if (!editor || !st) return <div className="rich-editor" />;

  /**
   * Each file: a placeholder node with live %, inserted AT the caret (or drop point) in
   * pick order with the caret left after the last one, then replaced in place — found by
   * its uploadId, never by position — by the ref on success, removed on error.
   */
  startUploads.current = (files, at) => {
    const taskId = attach?.taskId;
    if (taskId == null || editor.isDestroyed) return;
    const items = files.map((file) => ({
      file,
      uploadId: `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    }));
    insertPlaceholders(editor, items.map((i) => ({ uploadId: i.uploadId, filename: i.file.name || 'file' })), at);
    for (const { file, uploadId } of items) {
      setUploadProgress(uploadId, 0);
      setUploads((n) => n + 1);
      uploadFile(client, { taskId, file, cfg: filesCfgRef.current, onProgress: (p) => setUploadProgress(uploadId, p) })
        .then(({ file: f, ref }) => {
          const alt = /^!\[([^\]]*)\]/.exec(ref)?.[1] ?? f.filename.replace(/[[\]]/g, '_');
          swapPlaceholder(editor, uploadId, { src: `${FILE_REF_SCHEME}${f.id}`, alt });
        })
        .catch((e: Error) => {
          swapPlaceholder(editor, uploadId, null);
          setFileError({ id: Date.now(), text: `${file.name}: ${e.message}` });
        })
        .finally(() => {
          setUploadProgress(uploadId, null);
          setUploads((n) => n - 1);
        });
    }
  };

  const attachTitle =
    attach?.taskId == null
      ? (attach?.hint ?? 'Attach after creating')
      : uploadService === 'down'
        ? UPLOAD_SERVICE_DOWN_HINT
        : uploadService === 'checking'
          ? 'Checking upload service…'
          : 'Attach image or video';

  const c = () => editor.chain().focus();
  const openLink = () => {
    setLinkValue((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkError('');
    setLinkOpen(true);
  };
  const closeLink = () => {
    setLinkOpen(false);
    editor.commands.focus();
  };
  const applyLink = () => {
    const r = normalizeHref(linkValue);
    if (!r.ok) {
      setLinkError(r.error);
      return;
    }
    const { empty } = editor.state.selection;
    if (editor.isActive('link')) c().extendMarkRange('link').setLink({ href: r.href }).run();
    else if (empty) {
      c()
        .insertContent({ type: 'text', text: linkValue.trim(), marks: [{ type: 'link', attrs: { href: r.href } }] })
        .unsetMark('link')
        .run();
    } else c().setLink({ href: r.href }).run();
    setLinkOpen(false);
  };
  const removeLink = () => {
    c().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  // ReactNode label: text marks (B, I, S) mix with icon glyphs.
  const btn = (label: ReactNode, title: string, active: boolean, fn: () => void) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={active ? 'active' : ''}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={fn}
    >
      {label}
    </button>
  );
  const sep = <span className="rich-sep" aria-hidden="true" />;

  return (
    <div className={`rich-editor${disabled ? ' is-disabled' : ''}`}>
      <div className={`rich-toolbar-wrap${clip.start ? ' clip-start' : ''}${clip.end ? ' clip-end' : ''}`}>
      <div className="rich-toolbar" ref={toolbarRef}>
        <select
          className="class-select class-select--sm rich-heading"
          aria-label="Text style"
          title="Text style"
          disabled={disabled}
          value={st.heading}
          onChange={(e) => {
            const lv = Number(e.target.value);
            if (lv === 1 || lv === 2 || lv === 3) c().setHeading({ level: lv }).run();
            else c().setParagraph().run();
          }}
        >
          <option value="0">Text</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
        {sep}
        {btn(<b>B</b>, 'Bold (⌘B)', st.bold, () => c().toggleBold().run())}
        {btn(<i className="rich-glyph-i">I</i>, 'Italic (⌘I)', st.italic, () => c().toggleItalic().run())}
        {btn(<s>S</s>, 'Strikethrough (⌘⇧S)', st.strike, () => c().toggleStrike().run())}
        {btn(<Glyph d={GLYPHS.code} />, 'Inline code (⌘E)', st.code, () => c().toggleCode().run())}
        {btn(<Glyph d={GLYPHS.link} />, 'Link', st.link || linkOpen, () => (linkOpen ? closeLink() : openLink()))}
        {sep}
        {btn(<Glyph d={GLYPHS.bullet} />, 'Bulleted list', st.bulletList, () => c().toggleBulletList().run())}
        {btn(<Glyph d={GLYPHS.ordered} />, 'Numbered list', st.orderedList, () => c().toggleOrderedList().run())}
        {btn(<Glyph d={GLYPHS.task} />, 'Task list', st.taskList, () => c().toggleTaskList().run())}
        {btn(<Icon name="quote" />, 'Quote', st.blockquote, () => c().toggleBlockquote().run())}
        {btn(<Glyph d={GLYPHS.codeBlock} />, 'Code block (⌘⌥C)', st.codeBlock, () => c().toggleCodeBlock().run())}
        {attach && filesCfg?.uploadsEnabled && (
          <>
            {sep}
            <button
              type="button"
              className="rich-attach"
              data-testid="rich-attach"
              data-upload-service={uploadService}
              title={attachTitle}
              aria-label={attachTitle}
              disabled={!canAttach}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const { from, to } = editor.state.selection;
                pickAt.current = { from, to };
                fileInput.current?.click();
              }}
            >
              <Glyph d={GLYPHS.attach} />
              {attach.taskId == null ? (
                <span className="rich-attach__hint">{attach.hint ?? 'Attach after creating'}</span>
              ) : (
                uploadService === 'down' && (
                  <span className="rich-attach__hint" data-testid="rich-attach-unavailable">
                    {UPLOAD_SERVICE_DOWN_HINT}
                  </span>
                )
              )}
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              tabIndex={-1}
              aria-label="Attach files"
              data-testid="rich-attach-input"
              accept={[...filesCfg.imageTypes, ...filesCfg.videoTypes].join(',')}
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                e.target.value = '';
                const at = pickAt.current ?? undefined;
                pickAt.current = null;
                if (picked.length) startUploads.current(picked, at);
              }}
            />
          </>
        )}
      </div>
      </div>

      {linkOpen && (
        <div className="rich-link-pop" role="dialog" aria-label="Link">
          <input
            ref={linkInput}
            type="text"
            className="rich-link-input"
            placeholder="https://example.com"
            aria-label="Link URL"
            aria-invalid={!!linkError}
            value={linkValue}
            onChange={(e) => {
              setLinkValue(e.target.value);
              setLinkError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                closeLink();
              }
            }}
          />
          <button type="button" className="btn-primary btn-sm" onClick={applyLink}>
            Apply
          </button>
          {editor.isActive('link') && (
            <button type="button" className="btn-ghost btn-sm" onClick={removeLink}>
              Remove link
            </button>
          )}
          {linkError && <span className="rich-link-error" role="alert">{linkError}</span>}
        </div>
      )}

      <EditorContent editor={editor} className="rich-content" />
      {uploads > 0 && (
        <span className="sr-only" role="status" data-testid="rich-uploading">
          Uploading {uploads} file{uploads > 1 ? 's' : ''}…
        </span>
      )}
      {/* portalled: .rich-editor is a size container, which would trap position:fixed */}
      {fileError && createPortal(
        <div className="file-toast" role="alert" data-testid="file-toast">
          <span>{fileError.text}</span>
          <button type="button" className="mini-btn mini-btn--icon" aria-label="Dismiss" onClick={() => setFileError(null)}>
            ×
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * A caret just after the block at `after`: in the next textblock, else a gap cursor, else
 * nothing (null) — never a selection that would re-select the node.
 */
function caretAfter(doc: PMNode, after: number): Selection | null {
  const $after = doc.resolve(after);
  if ($after.nodeAfter?.isTextblock) return TextSelection.create(doc, after + 1);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((GapCursor as any).valid?.($after)) return new GapCursor($after);
  return null;
}

/** A node-selected attachment → caret after it (so a paste lands after, not over it). */
function caretOffSelectedFile(view: EditorView) {
  const sel = view.state.selection;
  if (!(sel instanceof NodeSelection) || sel.node.type.name !== 'image') return;
  const next = caretAfter(view.state.doc, sel.to);
  if (next) view.dispatch(view.state.tr.setSelection(next));
}

/**
 * Insert one upload placeholder per file at `at` (default: the current selection),
 * in order. A selected range/node is NOT replaced — files go after it. After each
 * placeholder the caret moves to the start of the following text (an empty paragraph is
 * added when nothing textual follows), so text typed next and the next file both land
 * after it: "Line one:" A "Line two:" B stays in that order.
 */
function insertPlaceholders(editor: Editor, list: { uploadId: string; filename: string }[], at?: { from: number; to: number }) {
  if (editor.isDestroyed || !list.length) return;
  const { schema } = editor.state;
  const tr: Transaction = editor.state.tr;
  const max = tr.doc.content.size;
  const point = Math.min(Math.max(at ? at.to : tr.selection.to, 0), max);
  tr.setSelection(TextSelection.near(tr.doc.resolve(point)));
  if (tr.selection instanceof TextSelection && !tr.selection.empty) tr.setSelection(TextSelection.create(tr.doc, tr.selection.to));
  for (const attrs of list) {
    if (tr.selection instanceof NodeSelection) {
      // a selected node is never replaced: move past it (adding a paragraph if needed)
      const end = tr.selection.to;
      let next = caretAfter(tr.doc, end);
      if (!next) {
        tr.insert(end, schema.nodes.paragraph.create());
        next = TextSelection.create(tr.doc, end + 1);
      }
      tr.setSelection(next);
    }
    tr.replaceSelectionWith(schema.nodes.uploadPlaceholder.create(attrs), false);
    const pos = findPlaceholder(tr.doc, attrs.uploadId);
    if (!pos) continue;
    const after = pos.at + pos.size;
    const $after = tr.doc.resolve(after);
    if (!$after.nodeAfter?.isTextblock && $after.parent.canReplaceWith($after.index(), $after.index(), schema.nodes.paragraph)) {
      tr.insert(after, schema.nodes.paragraph.create());
    }
    const next = caretAfter(tr.doc, after);
    if (next) tr.setSelection(next);
  }
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
}

function findPlaceholder(doc: PMNode, uploadId: string): { at: number; size: number } | null {
  let hit: { at: number; size: number } | null = null;
  doc.descendants((n, pos) => {
    if (!hit && n.type.name === 'uploadPlaceholder' && n.attrs.uploadId === uploadId) hit = { at: pos, size: n.nodeSize };
    return !hit;
  });
  return hit;
}

/** Replace the upload placeholder `uploadId` with an attachment image node, or drop it (null). */
function swapPlaceholder(editor: Editor, uploadId: string, image: { src: string; alt: string } | null) {
  if (editor.isDestroyed) return;
  const hit = findPlaceholder(editor.state.doc, uploadId);
  if (!hit) return; // the user deleted the placeholder meanwhile
  const { at, size } = hit;
  const { tr, schema } = editor.state;
  if (image) tr.replaceWith(at, at + size, schema.nodes.image.create(image));
  else tr.delete(at, at + size);
  editor.view.dispatch(tr);
}

/**
 * `image` with a NodeView: `infras-file:<uuid>` renders the attachment (image / video /
 * Processing… / file unavailable) through the same <FileEmbed> as the read view; any
 * other src is a plain <img>. Same node name + markdown as Image, so it saves as
 * `![name](infras-file:<uuid>)` byte for byte.
 */
const InfrasFile = Image.extend({
  // Never put `infras-file:<uuid>` in a DOM src — the browser would request it
  // (ERR_UNKNOWN_URL_SCHEME), e.g. when the view renders before the React NodeView is
  // ready, or on copy/drag. The DOM form carries data-file-id; the NodeView signs the URL.
  renderHTML({ HTMLAttributes }) {
    const id = fileIdOf(String(HTMLAttributes.src ?? ''));
    if (!id) return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    const { src: _src, ...rest } = HTMLAttributes;
    return ['img', mergeAttributes(this.options.HTMLAttributes, rest, { 'data-file-id': id })];
  },
  parseHTML() {
    return [
      {
        tag: 'img[data-file-id]',
        priority: 60,
        getAttrs: (el: HTMLElement) => ({
          src: `${FILE_REF_SCHEME}${el.getAttribute('data-file-id')}`,
          alt: el.getAttribute('alt'),
          title: el.getAttribute('title'),
        }),
      },
      ...(this.parent?.() ?? []),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(InfrasFileView);
  },
});

function InfrasFileView({ node, selected }: NodeViewProps) {
  const src = String(node.attrs.src ?? '');
  const alt = (node.attrs.alt as string | null) ?? undefined;
  const id = fileIdOf(src);
  return (
    <NodeViewWrapper className={`rich-file${selected ? ' is-selected' : ''}`} data-drag-handle="" contentEditable={false}>
      {id ? <FileEmbed id={id} alt={alt} inEditor /> : <img src={src} alt={alt} />}
    </NodeViewWrapper>
  );
}

/**
 * An upload in flight: filename + live %. Editor-only — it serialises to nothing, and
 * Save is blocked while one exists, so it can never reach stored markdown.
 */
const UploadPlaceholder = TiptapNode.create({
  name: 'uploadPlaceholder',
  group: 'block',
  atom: true,
  selectable: false,
  draggable: false,
  addAttributes() {
    return { uploadId: { default: '' }, filename: { default: '' } };
  },
  parseHTML() {
    return [];
  },
  renderHTML() {
    return ['div', { 'data-upload-placeholder': '' }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(UploadPlaceholderView);
  },
  renderMarkdown: () => '',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any);

function UploadPlaceholderView({ node }: NodeViewProps) {
  const pct = useUploadProgress(String(node.attrs.uploadId));
  return (
    <NodeViewWrapper className="rich-upload" data-testid="upload-placeholder" contentEditable={false}>
      <span className="file-spin" aria-hidden="true" />
      <span className="rich-upload__name">{String(node.attrs.filename)}</span>
      <span className="rich-upload__bar" aria-hidden="true">
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="rich-upload__pct" data-testid="upload-progress">
        {pct}%
      </span>
    </NodeViewWrapper>
  );
}

/** Caret (outside a code block) inside a table cell or header cell. */
function inTableCell(e: Editor): boolean {
  if (e.isActive('codeBlock')) return false;
  const { $from } = e.state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d).type.name;
    if (n === 'tableCell' || n === 'tableHeader') return true;
  }
  return false;
}

/**
 * `- ` typed at the start of the only item of a numbered list (typically the one just
 * nested with Tab) turns that list into a bulleted one; `1. ` does the reverse. The
 * stock wrapping rules can't fire there — a list item must start with a paragraph, so
 * the text stays literal and saves escaped (`1. \- nested`).
 */
const ListTypeSwitch = Extension.create({
  name: 'listTypeSwitch',
  priority: 1000,
  addInputRules() {
    const rule = (find: RegExp, from: string, run: (c: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>) =>
      new InputRule({
        find,
        handler: ({ state, range, chain }) => {
          const $from = state.doc.resolve(range.from);
          if ($from.depth < 3 || $from.parent.type.name !== 'paragraph') return null;
          const item = $from.node(-1);
          const list = $from.node(-2);
          if (item.type.name !== 'listItem' || $from.index(-1) !== 0) return null;
          if (list.type.name !== from || list.childCount !== 1) return null;
          run(chain().deleteRange(range)).run();
        },
      });
    return [
      rule(/^\s*([-+*])\s$/, 'orderedList', (c) => c.toggleBulletList()),
      rule(/^(\d+)\.\s$/, 'bulletList', (c) => c.toggleOrderedList()),
    ];
  },
});

/**
 * Select-all + Delete leaves an AllSelection over the now-empty doc. Turning that into a
 * code block (toolbar) kept it, so the first keystroke replaced the whole doc — block
 * included — with a paragraph. An all-selection of nothing becomes a caret at the start.
 */
const EmptyDocCaret = Extension.create({
  name: 'emptyDocCaret',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (_trs, _old, state) => {
          if (!(state.selection instanceof AllSelection)) return null;
          let empty = true;
          state.doc.forEach((n) => {
            if (!n.isTextblock || n.content.size) empty = false;
          });
          return empty ? state.tr.setSelection(TextSelection.atStart(state.doc)) : null;
        },
      }),
    ];
  },
});

/* ── @mention suggestion popup — a positioned <body> div; selection + keyboard
   are managed in this closure (NOT via a React ref, which proved unreliable),
   so ↑/↓/Enter/Tab work; the React component is display-only. ── */

function MentionList({
  items,
  selected,
  onPick,
}: {
  items: MentionUser[];
  selected: number;
  onPick: (i: number) => void;
}) {
  if (!items.length) return <div className="mention-popup empty">No matches</div>;
  return (
    <div className="mention-popup">
      {items.map((u, i) => (
        <button
          type="button"
          key={u.email}
          className={i === selected ? 'active' : ''}
          onMouseDown={(e) => { e.preventDefault(); onPick(i); }}
        >
          <span className="mention-name">{u.displayName || u.email}</span>
          {u.displayName && <span className="mention-email">{u.email}</span>}
        </button>
      ))}
    </div>
  );
}

function makeMentionRenderer(openRef: { current: boolean }) {
  let component: ReactRenderer | null = null;
  let el: HTMLDivElement | null = null;
  let items: MentionUser[] = [];
  let selected = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let command: (a: { id: string; label: string }) => void = () => {};

  const place = (rect: (() => DOMRect | null) | null | undefined) => {
    if (!el || !rect) return;
    const r = rect();
    if (!r) return;
    el.style.top = `${r.bottom + window.scrollY + 4}px`;
    el.style.left = `${r.left + window.scrollX}px`;
  };
  const pick = (i: number) => {
    const u = items[i];
    if (u) command({ id: u.email, label: u.displayName || u.email });
  };
  const render = () => component?.updateProps({ items, selected, onPick: pick });

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onStart: (props: any) => {
      openRef.current = true;
      items = props.items; selected = 0; command = props.command;
      component = new ReactRenderer(MentionList, { props: { items, selected, onPick: pick }, editor: props.editor });
      el = document.createElement('div');
      el.className = 'mention-popup-anchor';
      document.body.appendChild(el);
      el.appendChild(component.element as HTMLElement);
      place(props.clientRect);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onUpdate: (props: any) => { items = props.items; selected = 0; command = props.command; render(); place(props.clientRect); },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onKeyDown: (props: any) => {
      const k = props.event.key;
      if (!items.length) return k === 'Escape';
      if (k === 'ArrowDown') { selected = (selected + 1) % items.length; render(); return true; }
      if (k === 'ArrowUp') { selected = (selected + items.length - 1) % items.length; render(); return true; }
      if (k === 'Enter' || k === 'Tab') { pick(selected); return true; }
      if (k === 'Escape') return true;
      return false;
    },
    onExit: () => { openRef.current = false; el?.remove(); component?.destroy(); el = null; component = null; },
  };
}
